/**
 * メールによるログイン。
 *
 * 2通りの受け口を用意している:
 *   - **マジックリンク**: メール内のリンクを開くと、supabase-js の
 *     detectSessionInUrl がURLからセッションを拾って自動的にログインする。
 *     Supabase の無料プランは**メールテンプレートを変更できず**、標準では
 *     リンクのみが送られるため、これが本番の主経路になる。
 *   - **確認コード**: テンプレートに {{ .Token }} を入れられる環境
 *     （カスタムSMTP / 有料プラン、およびローカル開発）ではコードも届く。
 *
 * 招待されていないユーザーもサインアップ自体はできてしまうが、
 * public.users に行が無いため RLS 上は何も見えない（設計どおり）。
 * その場合はログイン後に「招待されていません」と表示する。
 */
import { useEffect, useState } from 'react'
import { supabase, authUrlError } from '../lib/supabase.ts'

/**
 * Supabase が返すエラーコードを、利用者が次に何をすべきか分かる日本語にする。
 * 英語のまま出すと「何が起きたか」も「どうすればいいか」も伝わらない。
 */
function explainAuthError(code: string | null, fallback: string | null): string {
  switch (code) {
    case 'otp_expired':
    case 'access_denied':
      return (
        'ログインリンクの有効期限が切れているか、すでに使用済みです（有効期限は発行から1時間）。' +
        'チャットやメールのプレビュー機能がリンクを先に開いてしまうと、届いた時点で使用済みになることがあります。' +
        '担当者に再発行を依頼するか、6桁のログインコードを受け取って下記に入力してください。'
      )
    case 'over_email_send_rate_limit':
    case 'over_request_rate_limit':
      return (
        'メールの送信回数が上限に達しました（1時間あたり2通）。' +
        '担当者から6桁のログインコードを受け取って、「コードをお持ちの方」から入力してください。'
      )
    case 'otp_disabled':
    case 'signup_disabled':
      return 'このメールアドレスではログインできない設定になっています。担当者にお問い合わせください。'
    default:
      return fallback ?? 'ログインに失敗しました。担当者にお問い合わせください。'
  }
}

export function Login() {
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [step, setStep] = useState<'email' | 'code'>('email')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // 期限切れ・使用済みリンクで戻ってきた場合は、その理由を最初に見せる。
  useEffect(() => {
    if (!authUrlError) return
    setError(explainAuthError(authUrlError.code, authUrlError.description))
    // 理由を表示したらURLは掃除する（再読み込みで再発火させない）
    window.history.replaceState(null, '', window.location.pathname)
  }, [])

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { shouldCreateUser: true },
    })
    setBusy(false)
    if (error) {
      setError(explainAuthError((error as { code?: string }).code ?? null, error.message))
      return
    }
    setNotice(
      `${email} にログイン用のメールを送信しました。` +
        'メール内のリンクを開くとそのままログインできます。' +
        'コードが記載されている場合は下に入力してください。',
    )
    setStep('code')
  }

  const verify = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)

    // コードの発行元によって type が違う:
    //   signInWithOtp が送ったもの        → 'email'
    //   管理APIの generate_link(magiclink) → 'magiclink'
    // 利用者に区別を求めるのは筋が悪いので、両方試す。
    const token = code.trim()
    const first = await supabase.auth.verifyOtp({ email, token, type: 'email' })
    const result = first.error
      ? await supabase.auth.verifyOtp({ email, token, type: 'magiclink' })
      : first

    setBusy(false)
    if (result.error) {
      setError(
        /expired|invalid/i.test(result.error.message)
          ? 'コードが正しくないか、有効期限（1時間）が切れています。担当者に再発行を依頼してください。'
          : result.error.message,
      )
    }
    // 成功時は onAuthStateChange が App 側で拾う
  }

  return (
    <div className="login">
      <div className="login-card">
        <h1>素材ライブラリ</h1>
        <p className="muted">映像・画像素材の配信とライセンス管理</p>

        {step === 'email' ? (
          <form onSubmit={sendCode}>
            <label htmlFor="email">メールアドレス</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
            />
            {/* 担当者から手渡しでコードを受け取る運用が主。
                メール送信は 2通/時 の制限があり、詰まりやすいので副導線に置く。 */}
            <button
              type="button"
              disabled={!email}
              onClick={() => {
                setNotice(null)
                setStep('code')
              }}
            >
              担当者から受け取ったコードを入力
            </button>
            <button type="submit" className="link" disabled={busy || !email}>
              {busy ? '送信中…' : 'メールでログインコードを受け取る'}
            </button>
          </form>
        ) : (
          <form onSubmit={verify}>
            <p className="muted small">{email}</p>
            <label htmlFor="code">ログインコード（6桁）</label>
            <input
              id="code"
              inputMode="numeric"
              pattern="[0-9]*"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              required
              autoComplete="one-time-code"
            />
            <button type="submit" disabled={busy || code.trim().length < 6}>
              {busy ? '確認中…' : 'ログイン'}
            </button>
            <button
              type="button"
              className="link"
              onClick={() => {
                setStep('email')
                setCode('')
                setNotice(null)
              }}
            >
              メールアドレスを入力し直す
            </button>
          </form>
        )}

        {notice && <p className="notice">{notice}</p>}
        {error && <p className="error">{error}</p>}
      </div>
    </div>
  )
}
